import { createClient, AuthError } from "@supabase/supabase-js";

// Try to get from VITE env vars first, then fall back to hardcoded values from .env.example
const supabaseUrl =
  import.meta.env.VITE_SUPABASE_URL ||
  "https://fhdhzqwudruovwspquyv.supabase.co";
const supabaseAnonKey =
  import.meta.env.VITE_SUPABASE_ANON_KEY ||
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZoZGh6cXd1ZHJ1b3Z3c3BxdXl2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTAwODIyMzAsImV4cCI6MjA2NTY1ODIzMH0.jRx-UyS1g4gN8lquuh1i7Ul_Q4QD6GfqtkmgXilxZ80";

console.log("Supabase URL:", supabaseUrl);
console.log("Supabase Key exists:", !!supabaseAnonKey);
console.log("Supabase Key prefix:", supabaseAnonKey?.substring(0, 20));
console.log("Environment type:", import.meta.env.MODE);

if (!supabaseUrl || !supabaseAnonKey) {
  console.error("Missing Supabase environment variables");
  console.error("URL:", supabaseUrl);
  console.error("Key:", supabaseAnonKey);
  throw new Error("Missing Supabase environment variables");
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: {
      getItem: (key: string) => {
        // Add debugging
        const value = window.localStorage.getItem(key);
        console.log(
          `Getting session for key ${key}:`,
          value ? "exists" : "null",
        );

        // Check for invalid refresh token in stored session
        if (value && key.includes("auth-token")) {
          try {
            const parsed = JSON.parse(value);
            // If we have a session but no refresh token, clear it
            if (parsed.currentSession && !parsed.currentSession.refresh_token) {
              console.warn("Session missing refresh token, clearing...");
              window.localStorage.removeItem(key);
              return null;
            }
          } catch (e) {
            console.error("Error parsing stored session:", e);
          }
        }

        return value;
      },
      setItem: (key: string, value: string) => {
        console.log(`Setting session for key ${key}`);
        window.localStorage.setItem(key, value);
      },
      removeItem: (key: string) => {
        console.log(`Removing session for key ${key}`);
        window.localStorage.removeItem(key);
      },
    },
  },
  db: {
    schema: "public",
  },
  global: {
    headers: {
      "x-application-name": "metapotheken-qa",
    },
  },
});

// Force session refresh on page load and ensure auth headers are set
if (typeof window !== "undefined") {
  let isRefreshing = false;

  // Add request interceptor to ensure auth token is always fresh
  supabase.auth.onAuthStateChange(async (event, session) => {
    console.log("Auth state changed:", event, session?.user?.email);
    console.trace("Auth state change triggered from:"); // This will show the call stack

    if (event === "TOKEN_REFRESHED") {
      console.log("Token refreshed successfully");
    }

    // Handle invalid refresh token
    if (event === "SIGNED_OUT" && !session) {
      console.log("User signed out or session invalid");
      // Clear all auth data
      const authKeys = Object.keys(localStorage).filter(
        (key) => key.includes("supabase") || key.includes("auth"),
      );
      authKeys.forEach((key) => {
        console.log(`Clearing auth key: ${key}`);
        localStorage.removeItem(key);
      });
    }
  });

  // Initial session check and refresh
  const initializeAuth = async () => {
    try {
      console.log("initializeAuth: Starting session check...");
      // First get the current session
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error) {
        console.error("Error getting initial session:", error);
        return;
      }

      if (session) {
        console.log("Session found on page load:", session.user?.email);

        // Skip automatic refresh on page load if token is still valid for > 10 minutes
        const tokenExpiresAt = session.expires_at
          ? session.expires_at * 1000
          : 0;
        const timeUntilExpiry = tokenExpiresAt - Date.now();

        if (timeUntilExpiry > 10 * 60 * 1000) {
          console.log(
            `Session still valid for ${Math.round(timeUntilExpiry / 1000 / 60)} minutes, skipping refresh`,
          );
          isRefreshing = false;
          return;
        }

        // Only refresh if token is about to expire
        if (!isRefreshing) {
          isRefreshing = true;
          console.log("Token expiring soon, refreshing session...");

          const { data: refreshData, error: refreshError } =
            await supabase.auth.refreshSession();

          if (refreshError) {
            console.error("Failed to refresh session:", refreshError);

            // Check if it's a refresh token error
            if (
              refreshError.message?.includes("Refresh Token") ||
              refreshError.message?.includes("refresh_token") ||
              refreshError.status === 400
            ) {
              console.error("Invalid refresh token detected, clearing auth...");

              // Clear all auth data
              const authKeys = Object.keys(localStorage).filter(
                (key) => key.includes("supabase") || key.includes("auth"),
              );
              authKeys.forEach((key) => localStorage.removeItem(key));

              // Sign out to clear server state
              await supabase.auth.signOut();

              // Force page reload to start fresh
              window.location.reload();
            }
          } else {
            console.log(
              "Session refreshed successfully, new expiry:",
              refreshData.session?.expires_at,
            );
          }

          isRefreshing = false;
        }
      } else {
        console.log("No session found on page load");
      }
    } catch (err) {
      console.error("Error initializing auth:", err);
    }
  };

  // Run initialization with a small delay to let other components initialize
  setTimeout(() => {
    initializeAuth();
  }, 100);

  // Ensure auth headers are included in all requests
  const originalFetch = window.fetch;
  window.fetch = async (...args) => {
    const [url, config] = args;

    // Only intercept Supabase requests
    if (typeof url === "string" && url.includes(supabaseUrl)) {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session?.access_token) {
        const headers = new Headers(config?.headers || {});
        headers.set("Authorization", `Bearer ${session.access_token}`);

        return originalFetch(url, {
          ...config,
          headers,
        });
      }
    }

    return originalFetch(...args);
  };
}

// Auth Types
export type AuthErrorType = AuthError | { message: string } | null;

// Database Types
export interface User {
  id: string;
  email: string;
  full_name: string;
  role: "admin" | "manager" | "employee" | "pharmacist";
  user_code?: string;
  pin_hash?: string;
  pin_expires_at?: string;
  user_id_2digit?: string;
  password_hash?: string;
  created_at: string;
  updated_at: string;
}

export interface Pharmacy {
  id: string;
  name: string;
  address: string;
  phone: string;
  email: string;
  created_at: string;
  updated_at: string;
}

export interface UserPharmacy {
  id: string;
  user_id: string;
  pharmacy_id: string;
  role: "admin" | "manager" | "employee";
  created_at: string;
}

export interface Task {
  id: string;
  title: string;
  description: string;
  type:
    | "communication"
    | "calibration"
    | "expiry_check"
    | "complaint"
    | "orders"
    | "other";
  status: "pending" | "in_progress" | "completed" | "cancelled";
  priority: "low" | "medium" | "high" | "urgent";
  pharmacy_id: string;
  assigned_to: string;
  created_by: string;
  due_date: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface TaskComment {
  id: string;
  task_id: string;
  user_id: string;
  comment: string;
  created_at: string;
}

export interface RecurringTask {
  id: string;
  title: string;
  description: string;
  type: "communication" | "calibration" | "expiry_check" | "complaint" | "orders" | "other";
  priority: "low" | "medium" | "high" | "urgent";
  pharmacy_id: string;
  recurrence_pattern: "daily" | "weekly" | "monthly" | "quarterly" | "yearly" | "custom";
  recurrence_interval: number;
  recurrence_days?: number[]; // For weekly: [1,2,3,4,5] for weekdays
  recurrence_day_of_month?: number; // For monthly: day of month (1-31)
  recurrence_week_of_month?: number; // For monthly: week of month (1-4)
  recurrence_day_of_week?: number; // For weekly: day of week (0=Sunday, 1=Monday, etc.)
  recurrence_month_of_year?: number[]; // For yearly: [1,6,12] for Jan, Jun, Dec
  assignment_type: "specific_user" | "open_to_all" | "team";
  assigned_to?: string;
  due_date_offset_days: number; // Days before/after the recurrence date
  due_time: string; // Time of day for due date
  status: "active" | "paused" | "completed" | "cancelled";
  created_by: string;
  last_generated_at?: string;
  next_generation_date?: string;
  total_generated_count: number;
  start_date: string;
  end_date?: string;
  created_at: string;
  updated_at: string;
}

export interface RecurringTaskInstance {
  id: string;
  recurring_task_id: string;
  task_id: string;
  generated_at: string;
  scheduled_date: string;
  status: "pending" | "in_progress" | "completed" | "cancelled";
  completed_at?: string;
  completed_by?: string;
}

export interface Communication {
  id: string;
  title: string;
  message: string;
  type: "info" | "action_required" | "urgent" | "announcement";
  pharmacy_id: string | null; // null for company-wide messages
  created_by: string;
  is_pinned: boolean;
  is_company_wide: boolean;
  category: "general" | "policy" | "emergency" | "training" | "system";
  attachments?: string[]; // URLs to attached files
  created_at: string;
  updated_at: string;
}

export interface CommunicationRead {
  id: string;
  communication_id: string;
  user_id: string;
  read_at: string;
}

export interface CommunicationComment {
  id: string;
  communication_id: string;
  user_id: string;
  comment: string;
  created_at: string;
}

export interface Scale {
  id: string;
  pharmacy_id: string;
  brand: string;
  model: string;
  e_value: number; // in grams
  serial_number?: string;
  location?: string;
  status: "active" | "inactive" | "maintenance";
  validation_date?: string; // Date when scale needs validation by notified body (YYYY-MM-DD)
  created_at: string;
  updated_at: string;
}

export interface ControlWeight {
  id: string;
  pharmacy_id: string;
  category: "f1_500g" | "f1_50g";
  nominal_weight: number; // 500.00 or 50.00
  actual_weight: number; // measured weight
  certificate_number?: string;
  certificate_expiry?: string;
  status: "active" | "expired" | "damaged";
  created_at: string;
  updated_at: string;
}

export interface CalibrationRecord {
  id: string;
  pharmacy_id: string;
  scale_id: string;
  control_weight_id: string;
  calibration_date: string;
  calibrated_by: string;
  measured_weight: number;
  expected_weight: number;
  deviation: number;
  status: "passed" | "failed" | "needs_adjustment";
  notes?: string;
  next_calibration_date: string;
  created_at: string;
  updated_at: string;
}

export interface ExpiryCheck {
  id: string;
  pharmacy_id: string;
  product_name: string;
  batch_number: string;
  expiry_date: string;
  quantity: number;
  location: string;
  checked_by: string;
  check_date: string;
  action_taken:
    | "none"
    | "marked"
    | "removed"
    | "returned"
    | "disposed"
    | "pending_pickup";
  created_at: string;
}

export interface CompoundingMaterialExpiry {
  id: string;
  pharmacy_id: string;
  product_name: string;
  manufacturer: string;
  lot_number: string;
  expiry_date: string;
  remaining_quantity_grams: number;
  location?: string;
  is_hazardous: boolean;
  status: "active" | "expired" | "near_expiry" | "disposed";
  checked_by: string;
  check_date: string;
  disposal_scheduled_date?: string;
  disposal_company?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface PharmaceuticalSpecialtyExpiry {
  id: string;
  pharmacy_id: string;
  product_name: string;
  cnk_code?: string;
  expiry_date: string;
  number_of_boxes: number;
  batch_number?: string;
  location?: string;
  handling_method:
    | "wholesaler_return"
    | "pharmintouch_return"
    | "sales_rep_pickup"
    | "immediate_discard";
  status: "active" | "expired" | "near_expiry" | "disposed";
  is_return_completed: boolean;
  return_completed_date?: string;
  return_completed_by?: string;
  checked_by: string;
  check_date: string;
  wholesaler_name?: string;
  sales_rep_contact?: string;
  return_scheduled_date?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface ExpiryCheckSession {
  id: string;
  pharmacy_id: string;
  check_date: string;
  checked_by: string;
  session_notes?: string;
  total_compounding_materials_checked: number;
  total_specialties_checked: number;
  total_expired_found: number;
  is_completed: boolean;
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface DisposalSchedule {
  id: string;
  pharmacy_id: string;
  scheduled_date: string;
  disposal_company: string;
  contact_person?: string;
  contact_phone?: string;
  contact_email?: string;
  status: "scheduled" | "completed" | "cancelled";
  total_items: number;
  notes?: string;
  created_by: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface DisposalScheduleItem {
  id: string;
  disposal_schedule_id: string;
  compounding_material_id?: string;
  pharmaceutical_specialty_id?: string;
  created_at: string;
}

export interface ExpirySummary {
  total_items: number;
  expired_items: number;
  expiring_soon: number;
  upcoming_items: number;
  hazardous_expired: number;
}

export interface Complaint {
  id: string;
  pharmacy_id: string;
  complaint_number: string;
  customer_name?: string;
  customer_contact?: string;
  description: string;
  category: "product" | "service" | "staff" | "other";
  severity: "low" | "medium" | "high" | "critical";
  status: "open" | "investigating" | "resolved" | "closed";
  assigned_to?: string;
  created_by: string;
  resolution?: string;
  resolved_at?: string;
  // CAPA fields
  root_cause?: string;
  corrective_action?: string;
  preventive_action?: string;
  capa_status?: "not_required" | "open" | "in_progress" | "completed";
  capa_due_date?: string;
  capa_responsible_id?: string;
  capa_responsible_name?: string;
  capa_completed_at?: string;
  created_at: string;
  updated_at: string;
}

// Quick authentication types
export interface QuickAuthResult {
  user_id: string | null;
  success: boolean;
  error_message: string | null;
}

export interface SetPinResult {
  success: boolean;
  error_message: string | null;
}

export interface UserWithPinStatus extends User {
  pin_status: "not_set" | "expired" | "active";
}

// Compounding Preparations Types
export interface Patient {
  id: string;
  pharmacy_id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface Prescriber {
  id: string;
  pharmacy_id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface Ingredient {
  id: string;
  pharmacy_id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface Preparation {
  id: string;
  pharmacy_id: string;
  preparation_type: "bulk" | "personalized";
  internal_lot_number: string;
  batch_number?: string;
  preparation_form: "capsules" | "suppositoiries" | "siroop" | "creme" | "suspensie" | "zalf" | "oplossing" | "poeder" | "other";
  amount?: number;
  amount_unit?: string;
  expiry_date: string;
  preparation_date: string;
  operator_id: string;
  validator_id?: string;
  validated_at?: string;
  status: "draft" | "completed" | "validated" | "cancelled";
  patient_id?: string;
  patient_name?: string;
  prescriber_id?: string;
  prescriber_name?: string;
  posology?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface PreparationIngredient {
  id: string;
  preparation_id: string;
  ingredient_id?: string;
  ingredient_name: string;
  internal_lot_number: string;
  theoretical_amount: number;
  actual_amount: number;
  unit: string;
  unit_dose?: number; // Dose per unit (eenheidsdosis) e.g., 50 mg per capsule
  unit_dose_unit?: string; // Unit for unit dose (mg, g, etc.)
  number_of_units?: number; // Number of capsules/suppositories
  order_index: number;
  created_at: string;
  updated_at: string;
}

// Database table names
export const Tables = {
  USERS: "users",
  PHARMACIES: "pharmacies",
  USER_PHARMACIES: "user_pharmacies",
  TASKS: "tasks",
  TASK_COMMENTS: "task_comments",
  COMMUNICATIONS: "communications",
  COMMUNICATION_READS: "communication_reads",
  COMMUNICATION_COMMENTS: "communication_comments",
  SCALES: "scales",
  CONTROL_WEIGHTS: "control_weights",
  CALIBRATION_RECORDS: "calibration_records",
  EXPIRY_CHECKS: "expiry_checks",
  COMPOUNDING_MATERIALS_EXPIRY: "compounding_materials_expiry",
  PHARMACEUTICAL_SPECIALTIES_EXPIRY: "pharmaceutical_specialties_expiry",
  EXPIRY_CHECK_SESSIONS: "expiry_check_sessions",
  DISPOSAL_SCHEDULES: "disposal_schedules",
  DISPOSAL_SCHEDULE_ITEMS: "disposal_schedule_items",
  COMPLAINTS: "complaints",
  PREPARATIONS: "preparations",
  PREPARATION_INGREDIENTS: "preparation_ingredients",
  PATIENTS: "patients",
  PRESCRIBERS: "prescribers",
  INGREDIENTS: "ingredients",
} as const;

// Quick authentication functions
export const quickAuthenticateUser = async (
  userCode: string,
  pin: string,
): Promise<QuickAuthResult> => {
  try {
    const { data, error } = await supabase.rpc("quick_authenticate_user", {
      input_user_code: userCode,
      input_pin: pin,
      client_ip: null, // Could be populated with real IP in production
      client_user_agent: navigator.userAgent,
    });

    if (error) {
      console.error("Quick auth error:", error);
      return {
        user_id: null,
        success: false,
        error_message: "Authentication failed. Please try again.",
      };
    }

    if (data && data.length > 0) {
      return data[0];
    }

    return {
      user_id: null,
      success: false,
      error_message: "Authentication failed. Please try again.",
    };
  } catch (err) {
    console.error("Quick auth network error:", err);
    return {
      user_id: null,
      success: false,
      error_message: "Network error. Please check your connection.",
    };
  }
};

export const setUserPin = async (
  targetUserId: string,
  newPin: string,
  setByUserId: string,
): Promise<SetPinResult> => {
  try {
    const { data, error } = await supabase.rpc("set_user_pin", {
      target_user_id: targetUserId,
      new_pin: newPin,
      set_by_user_id: setByUserId,
    });

    if (error) {
      console.error("Set PIN error:", error);
      return {
        success: false,
        error_message: "Failed to set PIN. Please try again.",
      };
    }

    if (data && data.length > 0) {
      return data[0];
    }

    return {
      success: false,
      error_message: "Failed to set PIN. Please try again.",
    };
  } catch (err) {
    console.error("Set PIN network error:", err);
    return {
      success: false,
      error_message: "Network error. Please check your connection.",
    };
  }
};

// New authentication functions for 2-digit user ID system
export const quickAuthenticateUserById = async (
  userId: string,
): Promise<QuickAuthResult> => {
  try {
    const { data, error } = await supabase.rpc("quick_authenticate_user_by_id", {
      input_user_id: userId,
      client_ip: null,
      client_user_agent: navigator.userAgent,
    });

    if (error) {
      console.error("Quick auth by ID error:", error);
      return {
        user_id: null,
        success: false,
        error_message: "Authentication failed. Please try again.",
      };
    }

    if (data && data.length > 0) {
      return data[0];
    }

    return {
      user_id: null,
      success: false,
      error_message: "Authentication failed. Please try again.",
    };
  } catch (err) {
    console.error("Quick auth by ID network error:", err);
    return {
      user_id: null,
      success: false,
      error_message: "Network error. Please check your connection.",
    };
  }
};

export const quickAuthenticateUserByIdAndPassword = async (
  userId: string,
  password: string,
): Promise<QuickAuthResult> => {
  try {
    const { data, error } = await supabase.rpc("quick_authenticate_user_by_id_and_password", {
      input_user_id: userId,
      input_password: password,
      client_ip: null,
      client_user_agent: navigator.userAgent,
    });

    if (error) {
      console.error("Quick auth by ID and password error:", error);
      return {
        user_id: null,
        success: false,
        error_message: "Authentication failed. Please try again.",
      };
    }

    if (data && data.length > 0) {
      return data[0];
    }

    return {
      user_id: null,
      success: false,
      error_message: "Authentication failed. Please try again.",
    };
  } catch (err) {
    console.error("Quick auth by ID and password network error:", err);
    return {
      user_id: null,
      success: false,
      error_message: "Network error. Please check your connection.",
    };
  }
};

export const getUsersWithPinStatus = async (): Promise<UserWithPinStatus[]> => {
  try {
    const { data, error } = await supabase
      .from("user_management_view")
      .select("*")
      .order("full_name");

    if (error) {
      console.error("Error fetching users with PIN status:", error);
      return [];
    }

    return data || [];
  } catch (err) {
    console.error("Network error fetching users:", err);
    return [];
  }
};

// Functions for managing 2-digit user IDs and passwords
export const setUserPassword = async (
  targetUserId: string,
  newPassword: string,
  setByUserId: string,
): Promise<SetPinResult> => {
  try {
    const { data, error } = await supabase.rpc("set_user_password", {
      target_user_id: targetUserId,
      new_password: newPassword,
      set_by_user_id: setByUserId,
    });

    if (error) {
      console.error("Set password error:", error);
      return {
        success: false,
        error_message: "Failed to set password. Please try again.",
      };
    }

    if (data && data.length > 0) {
      return data[0];
    }

    return {
      success: false,
      error_message: "Failed to set password. Please try again.",
    };
  } catch (err) {
    console.error("Set password network error:", err);
    return {
      success: false,
      error_message: "Network error. Please check your connection.",
    };
  }
};

export const assign2DigitUserId = async (
  userId: string,
  user2DigitId: string,
): Promise<{ success: boolean; error_message?: string }> => {
  try {
    const { error } = await supabase
      .from(Tables.USERS)
      .update({ user_id_2digit: user2DigitId })
      .eq("id", userId);

    if (error) {
      console.error("Error assigning 2-digit user ID:", error);
      return {
        success: false,
        error_message: "Failed to assign 2-digit user ID. Please try again.",
      };
    }

    return { success: true };
  } catch (err) {
    console.error("Assign 2-digit user ID network error:", err);
    return {
      success: false,
      error_message: "Network error. Please check your connection.",
    };
  }
};
